import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DataRefreshProvider } from './context/DataRefreshContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/DashboardNew';
import FichasListPage from './pages/FichasListPage';
import { MeasurementSheetForm } from './pages/MeasurementSheetForm';
import AdminUsers from './pages/AdminUsers';
import ExportExecutive from './pages/ExportExecutive';
import ExportModules from './pages/ExportModules';
import UserProfile from './pages/UserProfile';
import BrandSettings from './pages/BrandSettings';
import ExchangeRateSettings from './pages/ExchangeRateSettings';
import ForgotPassword from './pages/ForgotPassword';
import QuotesList from './pages/QuotesList';
import QuoteForm from './pages/QuoteForm';
import CargarTarifasExcel from './pages/CargarTarifasExcel';
// Módulo de Ventas
import VentasList from './pages/VentasList';
import VentaDetail from './pages/VentaDetail';
// Módulo de Aliados
import AliadosList from './pages/AliadosList';
import AliadoForm from './pages/AliadoForm';
import AliadoDetail from './pages/AliadoDetail';
import ReferidosList from './pages/ReferidosList';
import ReferidoForm from './pages/ReferidoForm';
import ReferidoFormV2 from './pages/ReferidoFormV2';
import ReferidosGlobal from './pages/ReferidosGlobal';
import PanelAliado from './pages/PanelAliado';
import DescargasAdmin from './pages/DescargasAdmin';
import CuentasCobro from './pages/CuentasCobro';
// Módulo de Clientes
import ClientesList from './pages/ClientesList';
import ClienteDetalle from './pages/ClienteDetalle';
// Módulo de Vendedores
import VendedoresList from './pages/VendedoresList';
// Módulo CRM
import CRMProspectos from './pages/CRMProspectos';
// Autorregistro público de clientes
import AutoregistroCliente from './pages/AutoregistroCliente';
// QR del autorregistro (requiere login)
import QRAutorregistro from './pages/QRAutorregistro';
// Portal Mi JPM (Acceso de Clientes - Separado del CRM)
import MiJPMPortal from './pages/MiJPMPortal';
// Dashboard Admin de Puntos JPM
import PuntosJPMAdmin from './pages/PuntosJPMAdmin';
// Admin de Condiciones de Venta
import CondicionesVentaAdmin from './pages/CondicionesVentaAdmin';
// Registros Eliminados (Borrado Lógico - Solo Gerencia)
import RegistrosEliminados from './pages/RegistrosEliminados';
import MantenimientoSemanal from './pages/MantenimientoSemanal';
// Bodas y Referidos
import BodasAdmin from './pages/BodasAdmin';
import EditorBoda from './pages/EditorBoda';
import BodasPreview from './pages/BodasPreview';
import { BodaPublica, ReferidoPublico } from './pages/PaginasBeneficio';
import TiendaOnlineAdmin from './pages/TiendaOnlineAdmin';
import TiendaPublica from './pages/TiendaPublica';
import { Toaster } from './components/ui/sonner';
import PWAInstallBanner from './components/PWAInstallBanner';
import './App.css';

// Remove external badges
const removeBadges = () => {
  const selectors = [
    '#emergent-badge',
    '[id*="emergent"]',
    'a[href*="emergent"]',
    '[class*="emergent"]'
  ];
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => el.remove());
  });
};

function App() {
  useEffect(() => {
    // Remove badges on mount and set interval
    removeBadges();
    const interval = setInterval(removeBadges, 500);
    
    // MutationObserver to catch dynamically added elements
    const observer = new MutationObserver(removeBadges);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <ThemeProvider>
    <AuthProvider>
    <DataRefreshProvider>
      <BrowserRouter>
        <Routes>
          {/* Ruta pública de autorregistro - NO requiere login */}
          <Route path="/aquiempiezatutrajeconjuanpablomurillo" element={<AutoregistroCliente />} />
          
          {/* Portal Mi JPM - Acceso de Clientes (Separado del CRM Admin) */}
          {/* Páginas públicas de beneficio (invitados de boda y referidos) */}
          <Route path="/bodas/:slug" element={<BodaPublica />} />
          <Route path="/referido/:codigo" element={<ReferidoPublico />} />
          <Route path="/bodas-preview" element={<BodasPreview />} />
          <Route path="/mi-jpm" element={<MiJPMPortal />} />
          <Route path="/mi-jpm/*" element={<MiJPMPortal />} />
          
          <Route path="/login" element={<LoginPage />} />
          <Route path="/tienda" element={<TiendaPublica />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fichas"
            element={
              <ProtectedRoute>
                <FichasListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/fichas"
            element={
              <ProtectedRoute>
                <FichasListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sheet/:id"
            element={
              <ProtectedRoute>
                <MeasurementSheetForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/brand"
            element={
              <ProtectedRoute>
                <BrandSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/exchange-rates"
            element={
              <ProtectedRoute>
                <ExchangeRateSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/export/executive"
            element={
              <ProtectedRoute>
                <ExportExecutive />
              </ProtectedRoute>
            }
          />
          <Route
            path="/export/modules"
            element={
              <ProtectedRoute>
                <ExportModules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <QuotesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <ProtectedRoute>
                <QuoteForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <QuoteForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cotizador/cargar-tarifas"
            element={
              <ProtectedRoute>
                <CargarTarifasExcel />
              </ProtectedRoute>
            }
          />
          {/* Módulo de Ventas */}
          <Route
            path="/ventas"
            element={
              <ProtectedRoute>
                <VentasList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ventas/:id"
            element={
              <ProtectedRoute>
                <VentaDetail />
              </ProtectedRoute>
            }
          />
          {/* Dashboard Puntos JPM */}
          <Route
            path="/puntos-jpm"
            element={
              <ProtectedRoute>
                <PuntosJPMAdmin />
              </ProtectedRoute>
            }
          />
          {/* Admin Condiciones de Venta */}
          <Route
            path="/admin/condiciones-venta"
            element={
              <ProtectedRoute>
                <CondicionesVentaAdmin />
              </ProtectedRoute>
            }
          />
          {/* Módulo de Aliados */}
          <Route
            path="/aliados"
            element={
              <ProtectedRoute>
                <AliadosList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referidos"
            element={
              <ProtectedRoute>
                <ReferidosGlobal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/nuevo"
            element={
              <ProtectedRoute>
                <AliadoForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/:id"
            element={
              <ProtectedRoute>
                <AliadoDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/:id/editar"
            element={
              <ProtectedRoute>
                <AliadoForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/:aliado_id/referidos"
            element={
              <ProtectedRoute>
                <ReferidosList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/:aliado_id/referidos/nuevo"
            element={
              <ProtectedRoute>
                <ReferidoFormV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/:aliado_id/referidos/:id"
            element={
              <ProtectedRoute>
                <ReferidoFormV2 />
              </ProtectedRoute>
            }
          />
          {/* Rutas legacy para compatibilidad */}
          <Route
            path="/aliados/:aliado_id/referidos-legacy/nuevo"
            element={
              <ProtectedRoute>
                <ReferidoForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aliados/:aliado_id/referidos-legacy/:id"
            element={
              <ProtectedRoute>
                <ReferidoForm />
              </ProtectedRoute>
            }
          />
          {/* Ruta global de referidos */}
          <Route
            path="/referidos"
            element={
              <ProtectedRoute allowedRoles={['gerencia', 'admin']}>
                <ReferidosGlobal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/panel-aliado"
            element={
              <ProtectedRoute>
                <PanelAliado />
              </ProtectedRoute>
            }
          />
          <Route
            path="/descargas"
            element={
              <ProtectedRoute allowedRoles={['gerencia', 'admin', 'contabilidad']}>
                <DescargasAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cuentas-cobro"
            element={
              <ProtectedRoute allowedRoles={['gerencia', 'admin', 'aliado', 'contabilidad']}>
                <CuentasCobro />
              </ProtectedRoute>
            }
          />
          {/* Módulo de Clientes */}
          <Route
            path="/clientes"
            element={
              <ProtectedRoute>
                <ClientesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes/:id"
            element={
              <ProtectedRoute>
                <ClienteDetalle />
              </ProtectedRoute>
            }
          />
          {/* QR de Autorregistro */}
          <Route
            path="/qr-autorregistro"
            element={
              <ProtectedRoute>
                <QRAutorregistro />
              </ProtectedRoute>
            }
          />
          {/* Módulo de Vendedores */}
          <Route
            path="/vendedores"
            element={
              <ProtectedRoute allowedRoles={['gerencia', 'admin']}>
                <VendedoresList />
              </ProtectedRoute>
            }
          />
          {/* Módulo CRM / Gestión de Prospectos */}
          <Route
            path="/crm"
            element={
              <ProtectedRoute>
                <CRMProspectos />
              </ProtectedRoute>
            }
          />
          {/* Registros Eliminados - Solo Gerencia */}
          <Route
            path="/registros-eliminados"
            element={
              <ProtectedRoute allowedRoles={['gerencia', 'admin']}>
                <RegistrosEliminados />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/mantenimiento"
            element={
              <ProtectedRoute allowedRoles={['gerencia', 'admin']}>
                <MantenimientoSemanal />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/bodas" element={<ProtectedRoute><BodasAdmin /></ProtectedRoute>} />
          <Route path="/admin/bodas/nueva" element={<ProtectedRoute allowedRoles={['gerencia', 'admin']}><EditorBoda /></ProtectedRoute>} />
          <Route path="/admin/bodas/:slug/editar" element={<ProtectedRoute allowedRoles={['gerencia', 'admin']}><EditorBoda /></ProtectedRoute>} />
          <Route path="/admin/tienda" element={<ProtectedRoute allowedRoles={['gerencia']}><TiendaOnlineAdmin /></ProtectedRoute>} />
          {/* Redirección inteligente basada en rol */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* Ruta catch-all para 404 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <PWAInstallBanner />
    </DataRefreshProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;